import React, { useState } from 'react';
import { Mail, Linkedin, Instagram, MapPin, Phone } from 'lucide-react';
import { siteConfig } from '@/data/mock';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';

const Footer = () => {
  const [socialLinks, setSocialLinks] = useState(siteConfig.company.social);
  const [tempLinks, setTempLinks] = useState(siteConfig.company.social);
  const [isDialogOpen, setIsDialogOpen] = useState(false);

  const currentYear = new Date().getFullYear();

  const handleSaveLinks = () => {
    setSocialLinks(tempLinks);
    localStorage.setItem('fhi_social_links', JSON.stringify(tempLinks));
    setIsDialogOpen(false);
    toast.success('Social media links updated successfully');
  };

  const scrollToSection = (e, href) => {
    e.preventDefault();
    const element = document.querySelector(href);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const navLinks = [
    { label: 'Home', href: '#home' },
    { label: 'About', href: '#about' },
    { label: 'Trade Focus', href: '#trade-focus' },
    { label: 'How We Work', href: '#how-we-work' },
    { label: 'Contact', href: '#contact' }
  ];

  return (
    <footer className="bg-background border-t border-border/40">
      <div className="container mx-auto px-4 lg:px-8 py-12">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-8">
          {/* Company Info */}
          <div className="space-y-4">
            <h3 className="text-lg font-semibold mb-3">{siteConfig.company.name}</h3>
            <p className="text-sm text-muted-foreground">
              {siteConfig.company.description}
            </p>
            <div className="space-y-3">
              <a
                href={`mailto:${siteConfig.company.email}`}
                className="flex items-start gap-2 text-sm text-muted-foreground hover:text-foreground transition-colors break-all"
              >
                <Mail className="h-4 w-4 flex-shrink-0 mt-0.5" />
                <span className="break-all">{siteConfig.company.email}</span>
              </a>
              <div className="flex items-start gap-2 text-sm text-muted-foreground">
                <Phone className="h-4 w-4 flex-shrink-0 mt-0.5" />
                <div className="flex flex-col gap-1">
                  <a href={`tel:${siteConfig.company.phones.india}`} className="hover:text-foreground transition-colors">
                    India: {siteConfig.company.phones.india}
                  </a>
                  <a href={`tel:${siteConfig.company.phones.newZealand}`} className="hover:text-foreground transition-colors">
                    NZ: {siteConfig.company.phones.newZealand}
                  </a>
                </div>
              </div>
              <div className="flex items-start gap-2 text-sm text-muted-foreground">
                <MapPin className="h-4 w-4 flex-shrink-0 mt-0.5" />
                <span className="leading-relaxed">{siteConfig.company.address}</span>
              </div>
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h3 className="text-lg font-semibold mb-3">Quick Links</h3>
            <ul className="space-y-2">
              {navLinks.map((link) => (
                <li key={link.label}>
                  <a
                    href={link.href}
                    onClick={(e) => scrollToSection(e, link.href)}
                    className="text-sm text-muted-foreground hover:text-foreground transition-colors"
                  >
                    {link.label}
                  </a>
                </li>
              ))}
            </ul>
          </div>

          {/* Social Links */}
          <div>
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-lg font-semibold">Connect With Us</h3>
              <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
                <DialogTrigger asChild>
                  <Button variant="ghost" size="sm" className="text-xs">
                    Update Links
                  </Button>
                </DialogTrigger>
                <DialogContent>
                  <DialogHeader>
                    <DialogTitle>Update Social Media Links</DialogTitle>
                    <DialogDescription>
                      Enter your company's social media profile URLs
                    </DialogDescription>
                  </DialogHeader>
                  <div className="space-y-4 py-4">
                    <div className="space-y-2">
                      <Label htmlFor="linkedin">LinkedIn URL</Label>
                      <Input
                        id="linkedin"
                        placeholder="https://linkedin.com/company/your-company"
                        value={tempLinks.linkedin}
                        onChange={(e) =>
                          setTempLinks({ ...tempLinks, linkedin: e.target.value })
                        }
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="instagram">Instagram URL</Label>
                      <Input
                        id="instagram"
                        placeholder="https://instagram.com/yourcompany"
                        value={tempLinks.instagram}
                        onChange={(e) =>
                          setTempLinks({ ...tempLinks, instagram: e.target.value })
                        }
                      />
                    </div>
                  </div>
                  <div className="flex justify-end gap-2">
                    <Button variant="outline" onClick={() => setIsDialogOpen(false)}>
                      Cancel
                    </Button>
                    <Button onClick={handleSaveLinks}>Save Changes</Button>
                  </div>
                </DialogContent>
              </Dialog>
            </div>
            <div className="flex gap-3">
              <a
                href={socialLinks.linkedin}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center justify-center h-10 w-10 rounded-full bg-accent/50 hover:bg-accent transition-all hover:scale-110"
                aria-label="LinkedIn"
              >
                <Linkedin className="h-5 w-5" />
              </a>
              <a
                href={socialLinks.instagram}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center justify-center h-10 w-10 rounded-full bg-accent/50 hover:bg-accent transition-all hover:scale-110"
                aria-label="Instagram"
              >
                <Instagram className="h-5 w-5" />
              </a>
            </div>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="pt-8 border-t border-border/40 text-center">
          <p className="text-sm text-muted-foreground">
            © {currentYear} {siteConfig.company.name}. All rights reserved.
          </p>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
